import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  Asset,
  Customer,
  FindingDto,
  GetScanResultSummaryData,
  InitialScanResult,
  NessusConfig,
  PentestScan,
  PentestScanPageRequest,
  PentestScanResultSummary,
  PentestStatistics,
  Runner,
  Template,
} from '../models';
import { PentestScanReport } from '../enums';
import { TranslateService } from '@ngx-translate/core';
import { CustomerPentestStatistics } from '../models/customer-pentest-statistics';
import { DemoStore } from '../stores';
import { DemoDataService } from './demo-data.service';
import { PartnerPentestStatistics } from '../models';
import { ScanResultType } from '../enums/scan-result-type.enum';

@Injectable({ providedIn: 'root' })
export class PentestService {
  private readonly httpClient = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private readonly demoStore = inject(DemoStore);
  private readonly demoDataService = inject(DemoDataService);

  getPentestScans(customerId: number | undefined, pageRequest: PentestScanPageRequest): Observable<PentestScan[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPentestScans(pageRequest.id));
    }
    return this.httpClient
      .get<
        PentestScan[]
      >(`/api/customers/${customerId || 'me'}/pentests/scans`, { params: PentestScanPageRequest.mapToQuery(pageRequest) })
      .pipe(map((pentestScans) => pentestScans.map((scan) => PentestScan.mapFromApiValue(scan))));
  }

  getPentestStatistics(customer?: Customer): Observable<PentestStatistics> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPentestStatistics());
    }
    return this.httpClient
      .get<any>(`/api/customers/${customer?.id || 'me'}/pentests/statistics`)
      .pipe(map((pentestStatistics) => PentestStatistics.mapFromApiValue(pentestStatistics)));
  }

  getCustomerPentestScanStatistics(customer?: Customer): Observable<CustomerPentestStatistics[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPentestScanStatistics());
    }
    return this.httpClient
      .get<CustomerPentestStatistics[]>(`/api/customers/${customer?.id || 'me'}/pentests/scans/statistics`)
      .pipe(
        map((customerPentestStatistics) =>
          customerPentestStatistics.map((customerPentestStatistics) =>
            CustomerPentestStatistics.mapFromApiValue(customerPentestStatistics),
          ),
        ),
      );
  }
  getPartnerPentestScanStatistics(partnerId?: string | undefined): Observable<PartnerPentestStatistics[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerPentestScanStatistics());
    }
    return this.httpClient
      .get<PartnerPentestStatistics[]>(`/api/partners/${partnerId || 'me'}/pentests/scans/statistics`)
      .pipe(
        map((partnerPentestStatistics) =>
          partnerPentestStatistics.map((partnerPentestStatistics) =>
            PartnerPentestStatistics.mapFromApiValue(partnerPentestStatistics),
          ),
        ),
      );
  }

  getPentestScanResultsSummary(
    scans: GetScanResultSummaryData[],
    customer: Customer | null | undefined,
  ): Observable<PentestScanResultSummary[]> {
    if (this.demoStore.isDemoMode()) {
      return of([]);
    }
    return this.httpClient
      .post<any[]>(`/api/customers/${customer?.id || 'me'}/pentests/scans/result-summary`, { scans })
      .pipe(
        map((pentestScanResults) =>
          pentestScanResults.map((pentestScanResult) => PentestScanResultSummary.mapFromApiValue(pentestScanResult)),
        ),
      );
  }

  getPentestScanFindings(
    customer: Customer | null | undefined,
    pentestScanId: string,
    scanResultType: ScanResultType,
    historyId?: string,
  ): Observable<FindingDto[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPentestScanFindings(pentestScanId));
    }
    return this.httpClient
      .get<FindingDto[]>(`/api/customers/${customer?.id || 'me'}/pentests/scans/${pentestScanId}/findings`, {
        params: { ...(historyId ? { historyId, scanResultType } : { scanResultType }) },
      })
      .pipe(map((findingDtos) => findingDtos.map((findingDto) => FindingDto.mapFromApiValue(findingDto))));
  }

  downloadPentestReport(
    pentestId: string,
    report: PentestScanReport,
    encryptReport: boolean,
    historyId?: string,
    customerId?: number,
  ): Observable<Blob> {
    if (this.demoStore.isDemoMode()) {
      return of(new Blob());
    }
    return this.httpClient
      .get(
        `/api/customers/${customerId || 'me'}/pentests/scans/${pentestId}/${historyId ? 'nessus-download' : 'horizon-download'}`,
        {
          params: {
            ...(historyId ? { historyId } : {}),
            ...(customerId ? { customerId } : {}),
            ...(!historyId && report ? { horizonReport: PentestScanReport.mapToApiValue(report) } : {}),
            ...(historyId && report ? { nessusReport: PentestScanReport.mapToApiValue(report) } : {}),
            ...{ encryptReport },
          },
          responseType: 'blob',
        },
      )
      .pipe(
        tap((blob: Blob) => {
          const linkElement = document.createElement('a');
          linkElement.href = window.URL.createObjectURL(blob);
          linkElement.download = `${this.translateService.instant(PentestScanReport.name(report))}.pdf`;
          document.body.appendChild(linkElement);
          linkElement.click();
          window.URL.revokeObjectURL(linkElement.href);
          document.body.removeChild(linkElement);
          return blob;
        }),
      );
  }

  initialScan(targets: string[]): Observable<{ results: InitialScanResult[] }> {
    if (this.demoStore.isDemoMode()) {
      return of({ results: [] });
    }
    return this.httpClient.post<{ results: InitialScanResult[] }>('/api/initial-scan', { targets });
  }

  getAppVersion(): Observable<{ version: string }> {
    return this.httpClient.get<{ version: string }>('/api/version');
  }

  getTemplates(customerId?: number): Observable<Template[]> {
    if (this.demoStore.isDemoMode() || !customerId) {
      return of([]);
    }
    return this.httpClient
      .get<Template[]>(`/api/config/horizon/${customerId}/templates`)
      .pipe(map((templates) => templates.map((template) => Template.mapFromApiValue(template))));
  }

  getRunners(customerId?: number): Observable<Runner[]> {
    if (this.demoStore.isDemoMode() || !customerId) {
      return of([]);
    }
    return this.httpClient.get<Runner[]>(`/api/config/horizon/${customerId}/runners`);
  }

  getAssets(customerId?: number): Observable<Asset[]> {
    if (this.demoStore.isDemoMode() || !customerId) {
      return of([]);
    }
    return this.httpClient.get<Asset[]>(`/api/config/horizon/${customerId}/assets`);
  }

  getNessusConfigs(customerId?: number): Observable<NessusConfig[]> {
    if (this.demoStore.isDemoMode() || !customerId) {
      return of([]);
    }
    return this.httpClient.get<NessusConfig[]>('/api/config/nessus');
  }
}
